import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@attmos/flowstack-ui";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { SESSION_QUERY_KEY } from "@/api/auth";
import { ApiError } from "@/api/client";
import { CognitoCallbackError, exchangeCognitoCallback, sanitizeReturnTo } from "@/api/cognito";

/**
 * ``/auth/callback`` — landing page after the Hosted UI redirect.
 *
 * Reads ``code`` + ``state`` from the URL, calls
 * :func:`exchangeCognitoCallback` (which handles the CSRF state
 * check + POST to ``/api/auth/cognito/callback``), then navigates
 * to the page the user was on when they kicked off login.
 *
 * The effect runs **exactly once** per mount (the ref guard); React
 * 18's StrictMode double-invokes effects in dev which would
 * otherwise consume the single-use ``code`` twice — second attempt
 * lands a 401. Production builds don't strict-mode-double-render
 * but the guard is cheap insurance against a future refactor.
 *
 * Failure modes the user can see:
 *   - "Cognito sign-in failed: …" (Cognito error param on callback)
 *   - "Sign-in session expired or this tab didn't start the flow" (no stash)
 *   - "Try signing in again" (state mismatch — CSRF defense fired)
 *   - "Could not reach the FlowStack server" (network failure)
 *   - The backend's error_code on ``cognito_token_invalid`` etc.
 *
 * On every failure path the page shows a "Try again" button that
 * routes back to the SignIn page so the user can restart the flow
 * cleanly.
 */
export function CognitoAuthCallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<"working" | "error">("working");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    // StrictMode + React 18 double-runs effects in dev. The
    // authorization ``code`` is single-use; the second invocation
    // would land a 401 + scare the user. Guard with a mount-scoped
    // ref so the exchange happens once.
    if (ranRef.current) return;
    ranRef.current = true;

    void (async () => {
      try {
        const { session, returnTo } = await exchangeCognitoCallback({
          search: location.search,
        });
        // Push the resolved session into React Query so
        // ``useSession`` consumers see it immediately without a
        // round-trip to ``/api/auth/session``.
        queryClient.setQueryData(SESSION_QUERY_KEY, session);
        // Per Rupert PR-328 r1 #1+#5: sanitize before navigating so
        // a stale-stash absolute URL (e.g. ``https://evil.com``)
        // surfaced by a future refactor is still caught at use time.
        // Belt-and-suspenders with the same check in
        // :func:`startCognitoLogin` at stash time.
        navigate(sanitizeReturnTo(returnTo), { replace: true });
      } catch (err) {
        setStatus("error");
        if (err instanceof CognitoCallbackError) {
          setErrorMessage(err.message);
          return;
        }
        if (err instanceof ApiError) {
          // The backend returns ``cognito_token_invalid`` /
          // ``cognito_config_error`` / etc. with a ``message`` field
          // tuned for end-user display. Surface it verbatim — the
          // error envelope strips Cognito-internal detail already.
          setErrorMessage(err.message || "Sign-in failed. Try again.");
          return;
        }
        setErrorMessage("Could not complete sign-in. Try again in a moment.");
      }
    })();
    // Dependencies pinned at mount: re-running on location change
    // would re-consume the (now invalid) code.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "working") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center bg-muted px-4 text-sm text-muted-foreground"
      >
        Completing sign-in…
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-4">
      <Card elevation="raised" className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Sign-in didn&apos;t complete</CardTitle>
          <CardDescription>
            Something interrupted the ATTMOS sign-in flow. Try again from the start.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            role="alert"
            aria-live="polite"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {errorMessage}
          </div>
        </CardContent>
        <CardFooter>
          <Button
            type="button"
            className="w-full"
            onClick={() => navigate("/auth/cognito/sign-in", { replace: true })}
          >
            Try again
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
